import {NodeType, OutputType} from 'common/src/modules/nodes/nodes.types';
import {
  ScheduleType,
  ScheduleTypeDescription,
} from 'common/src/modules/decisionTree/decisionTree.types';
import {TimezoneSource} from 'common/src/modules/timezones/timezones.types';
import {
  ConditionOperatorKey,
  ConditionOperatorKeyToText,
  ConditionType,
  ConditionCategory,
} from 'common/src/modules/conditions/conditions.types';
import {MacroItemTypeValue, MacroItemType} from 'common/src/modules/macros/macros.types';
import {
  LocationTargetingMethodValue,
  LocationTargetingMethods,
} from 'common/src/modules/locationFeed/locationFeed.types';
import {
  OptimizeByName,
  OptimizeAcrossName,
} from 'common/src/modules/optimization/optimization.types';
import getMatchingEnumValue from 'common/src/utils/typescriptUtils/getMatchingEnumValue';
import {
  AiNodeType,
  AiOutputType,
} from 'common/src/modules/campaigns/copilot/copilotCampaigns.types';
import {LandingPageSource} from 'common/src/modules/campaigns/campaigns.enums';
import {ProductSelectionStrategyType} from 'common/src/modules/productSelectionStrategies/productSelectionStrategies.types';
import {
  ContentSelectionStrategy,
  ContentSelectionStrategyValue,
} from 'common/src/modules/contentFeeds/contentFeeds.enums';

function getEnumKeyByValue(enumObj: Record<string, number>, value: number): string | null {
  const entry = Object.entries(enumObj).find(
    ([key, val]) => typeof val === 'number' && val === value && Number.isNaN(Number(key)),
  );

  return entry?.[0] ?? null;
}

export type {AiNodeType, AiOutputType};

export function toAiNodeType(
  nodeType: NodeType | null | undefined,
): (typeof AiNodeType)[keyof typeof AiNodeType] | null {
  if (nodeType == null) return null;

  return getMatchingEnumValue(NodeType, AiNodeType, nodeType) ?? null;
}

export function toAiOutputType(
  outputType: OutputType | null | undefined,
): (typeof AiOutputType)[keyof typeof AiOutputType] | null {
  if (outputType == null) return null;

  return getMatchingEnumValue(OutputType, AiOutputType, outputType) ?? null;
}

export function toAiScheduleType(scheduleType: ScheduleType | null | undefined): string | null {
  if (scheduleType == null) return null;

  return getMatchingEnumValue(ScheduleType, ScheduleTypeDescription, scheduleType) ?? null;
}

export function toAiTimezoneSource(source: number | null | undefined): string | null {
  if (source == null) return null;

  return getEnumKeyByValue(TimezoneSource as unknown as Record<string, number>, source);
}

export function toAiLocationTargetingMethod(method: number | null | undefined): string | null {
  if (method == null) return null;

  return (
    getMatchingEnumValue(LocationTargetingMethodValue, LocationTargetingMethods, method) ?? null
  );
}

export function toAiCondKeyType(value: MacroItemTypeValue | null | undefined): string | null {
  if (value == null) return null;

  return getMatchingEnumValue(MacroItemTypeValue, MacroItemType, value) ?? null;
}

export function toAiCondOperator(operator: ConditionOperatorKey | null | undefined): string | null {
  if (operator == null) return null;

  return ConditionOperatorKeyToText[operator] ?? null;
}

export function toAiConditionType(conditionType: ConditionType | null | undefined): string | null {
  if (conditionType == null) return null;

  return conditionType;
}

export function toAiConditionCategory(
  category: ConditionCategory | null | undefined,
): string | null {
  if (category == null) return null;

  return (
    getEnumKeyByValue(ConditionCategory as unknown as Record<string, number>, category as number) ??
    null
  );
}

export function toAiLandingPageSource(source: LandingPageSource | null | undefined): string | null {
  if (source == null) return null;

  return getEnumKeyByValue(LandingPageSource as unknown as Record<string, number>, source);
}

export function toAiProductStrategy(strategy: number | null | undefined): string | null {
  if (strategy == null) return null;

  return getEnumKeyByValue(
    ProductSelectionStrategyType as unknown as Record<string, number>,
    strategy,
  );
}

export function toAiContentStrategy(
  strategy: ContentSelectionStrategyValue | string | null | undefined,
): string | null {
  if (strategy == null) return null;

  return (
    getMatchingEnumValue(
      ContentSelectionStrategyValue,
      ContentSelectionStrategy,
      strategy as ContentSelectionStrategyValue,
    ) ?? null
  );
}

export function toAiOptimizeBy(name: OptimizeByName | null | undefined): string | null {
  if (name == null) return null;

  return name;
}

export function toAiOptimizeAcross(name: OptimizeAcrossName | null | undefined): string | null {
  if (name == null) return null;

  return name;
}
